import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Chart, registerables } from 'chart.js';
import {
  ConnectionAnalyticsService,
  AnalyticsFilters,
  AnalyticsSummary,
  AnalyticsByCountry,
  AnalyticsByDevice,
  AnalyticsBySessionDuration,
  AnalyticsTrends,
  AnalyticsPeakHours,
} from '../../services/connection-analytics.service';

Chart.register(...registerables);

const COUNTRY_NAMES: Record<string, string> = {
  CL: 'Chile',
  VE: 'Venezuela',
  US: 'Estados Unidos',
  AR: 'Argentina',
  PE: 'Perú',
  CO: 'Colombia',
  BR: 'Brasil',
  MX: 'México',
  EC: 'Ecuador',
  BO: 'Bolivia',
  PM: 'San Pedro y Miquelón',
};

const COUNTRY_FLAGS: Record<string, string> = {
  CL: '🇨🇱', VE: '🇻🇪', US: '🇺🇸', AR: '🇦🇷', PE: '🇵🇪',
  CO: '🇨🇴', BR: '🇧🇷', MX: '🇲🇽', EC: '🇪🇨', BO: '🇧🇴', PM: '🇵🇲',
};

const CHART_COLORS = {
  primary: '#1C593D',
  primaryLight: '#34d399',
  accent: '#059669',
  blue: '#2563eb',
  blueLight: '#93c5fd',
  purple: '#7c3aed',
  purpleLight: '#c4b5fd',
  orange: '#f59e0b',
  orangeLight: '#fde68a',
  red: '#dc2626',
  gray: '#6b7280',
  grayLight: '#e5e7eb',
  gradient: ['#1C593D', '#059669', '#34d399', '#2563eb', '#7c3aed', '#f59e0b', '#dc2626', '#6b7280'],
};

@Component({
  selector: 'app-connection-analytics',
  templateUrl: './connection-analytics.component.html',
  styleUrls: ['./connection-analytics.component.sass'],
})
export class ConnectionAnalyticsComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();

  // Filters
  dateFrom: string = '';
  dateTo: string = '';
  excludeOperators = true;
  groupBy: 'day' | 'week' | 'month' = 'day';
  activePreset: string = 'today';

  // Data
  summary: AnalyticsSummary | null = null;
  byCountry: AnalyticsByCountry | null = null;
  byDevice: AnalyticsByDevice | null = null;
  sessionDuration: AnalyticsBySessionDuration | null = null;
  trends: AnalyticsTrends | null = null;
  peakHours: AnalyticsPeakHours | null = null;

  // State
  loading = true;
  error = false;
  expandedSections: Set<string> = new Set(['countries', 'trends', 'devices', 'peakHours', 'session']);

  // Chart refs
  @ViewChild('countryChart') countryChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('trendsChart') trendsChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('peakHoursChart') peakHoursChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('sessionChart') sessionChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('deviceTypeChart') deviceTypeChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('osChart') osChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('browserChart') browserChartRef!: ElementRef<HTMLCanvasElement>;

  private charts: Map<string, Chart> = new Map();

  constructor(private analyticsService: ConnectionAnalyticsService) {}

  ngOnInit(): void {
    const today = new Date().toISOString().slice(0, 10);
    this.dateFrom = today;
    this.dateTo = today;
    this.loadAll();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.charts.forEach((chart) => chart.destroy());
  }

  toggleSection(section: string): void {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section);
    } else {
      this.expandedSections.add(section);
    }
  }

  setPreset(preset: string): void {
    this.activePreset = preset;
    const today = new Date();
    this.dateTo = today.toISOString().slice(0, 10);

    switch (preset) {
      case 'today':
        this.dateFrom = this.dateTo;
        break;
      case '7d':
        const d7 = new Date(today);
        d7.setDate(d7.getDate() - 6);
        this.dateFrom = d7.toISOString().slice(0, 10);
        break;
      case '30d':
        const d30 = new Date(today);
        d30.setDate(d30.getDate() - 29);
        this.dateFrom = d30.toISOString().slice(0, 10);
        break;
      case 'month':
        this.dateFrom = today.toISOString().slice(0, 8) + '01';
        break;
    }
    this.loadAll();
  }

  onDateChange(): void {
    this.activePreset = '';
    this.loadAll();
  }

  onExcludeOperatorsChange(): void {
    this.loadAll();
  }

  onGroupByChange(groupBy: 'day' | 'week' | 'month'): void {
    this.groupBy = groupBy;
    const today = new Date();
    this.dateTo = today.toISOString().slice(0, 10);

    switch (groupBy) {
      case 'day':
        const d30 = new Date(today);
        d30.setDate(d30.getDate() - 29);
        this.dateFrom = d30.toISOString().slice(0, 10);
        this.activePreset = '30d';
        break;
      case 'week':
        const w8 = new Date(today);
        w8.setDate(w8.getDate() - 55);
        this.dateFrom = w8.toISOString().slice(0, 10);
        this.activePreset = '';
        break;
      case 'month':
        const m6 = new Date(today);
        m6.setMonth(m6.getMonth() - 5);
        m6.setDate(1);
        this.dateFrom = m6.toISOString().slice(0, 10);
        this.activePreset = '';
        break;
    }

    this.loadAll();
  }

  loadAll(): void {
    this.loading = true;
    this.error = false;
    const filters = this.getFilters();

    forkJoin({
      summary: this.analyticsService.getSummary(filters),
      byCountry: this.analyticsService.getByCountry(filters),
      byDevice: this.analyticsService.getByDevice(filters),
      sessionDuration: this.analyticsService.getBySessionDuration(filters),
      trends: this.analyticsService.getTrends({ ...filters, groupBy: this.groupBy }),
      peakHours: this.analyticsService.getPeakHours(filters),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.summary = results.summary;
          this.byCountry = results.byCountry;
          this.byDevice = results.byDevice;
          this.sessionDuration = results.sessionDuration;
          this.trends = results.trends;
          this.peakHours = results.peakHours;
          this.loading = false;
          setTimeout(() => this.renderAllCharts(), 50);
        },
        error: () => {
          this.loading = false;
          this.error = true;
        },
      });
  }

  private getFilters(): AnalyticsFilters {
    return {
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      excludeOperators: this.excludeOperators,
    };
  }

  // Country helpers
  getCountryName(code: string): string {
    return COUNTRY_NAMES[code] || code;
  }

  getCountryFlag(code: string): string {
    return COUNTRY_FLAGS[code] || '🌐';
  }

  parsePeakHour(value: string): number {
    const n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  // Peak hours helper: UTC to Chile time
  toChileHour(utcHour: number): string {
    const chileOffset = -3;
    const chileHour = (utcHour + 24 + chileOffset) % 24;
    return `${chileHour.toString().padStart(2, '0')}:00`;
  }

  formatDuration(minutes: number): string {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes.toFixed(1)} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}m`;
  }

  formatNumber(value: number): string {
    return value.toLocaleString('es-CL');
  }

  getBarColor(index: number): string {
    return CHART_COLORS.gradient[index % CHART_COLORS.gradient.length];
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }

  getAvatarColor(index: number): string {
    const colors = ['#1C593D', '#059669', '#2563eb', '#7c3aed', '#f59e0b', '#dc2626', '#0891b2', '#6366f1', '#ea580c', '#84cc16'];
    return colors[index % colors.length];
  }

  // ─── Chart rendering ───

  private renderAllCharts(): void {
    this.renderCountryChart();
    this.renderTrendsChart();
    this.renderPeakHoursChart();
    this.renderSessionChart();
    this.renderDeviceTypeChart();
    this.renderOSChart();
    this.renderBrowserChart();
  }

  private destroyChart(key: string): void {
    const existing = this.charts.get(key);
    if (existing) {
      existing.destroy();
      this.charts.delete(key);
    }
  }

  private renderCountryChart(): void {
    if (!this.countryChartRef || !this.byCountry?.countries?.length) return;
    this.destroyChart('country');
    const data = this.byCountry.countries;
    const chart = new Chart(this.countryChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels: data.map((c) => this.getCountryName(c.country)),
        datasets: [
          {
            data: data.map((c) => c.uniqueUsers),
            backgroundColor: CHART_COLORS.gradient.slice(0, data.length),
            borderWidth: 2,
            borderColor: '#fff',
            hoverBorderWidth: 3,
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 13, weight: 'bold' },
            bodyFont: { size: 12 },
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => {
                const item = data[ctx.dataIndex];
                return ` ${item.uniqueUsers} usuarios (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
        },
      },
    });
    this.charts.set('country', chart);
  }

  private renderTrendsChart(): void {
    if (!this.trendsChartRef || !this.trends?.data?.length) return;
    this.destroyChart('trends');
    const labels = this.trends.data.map((d) => this.formatPeriodLabel(d.period));
    const chart = new Chart(this.trendsChartRef.nativeElement, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Usuarios únicos',
            data: this.trends.data.map((d) => d.uniqueUsers),
            borderColor: CHART_COLORS.primary,
            backgroundColor: 'rgba(28, 89, 61, 0.08)',
            fill: true,
            tension: 0.4,
            borderWidth: 2.5,
            pointBackgroundColor: CHART_COLORS.primary,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
          {
            label: 'Conexiones totales',
            data: this.trends.data.map((d) => d.totalConnections),
            borderColor: CHART_COLORS.blue,
            backgroundColor: 'rgba(37, 99, 235, 0.06)',
            fill: true,
            tension: 0.4,
            borderWidth: 2.5,
            pointBackgroundColor: CHART_COLORS.blue,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 16,
              font: { size: 12, weight: 'bold' },
            },
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 13, weight: 'bold' },
            bodyFont: { size: 12 },
            padding: 12,
            cornerRadius: 8,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 }, color: '#6b7280' },
          },
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6' },
            ticks: { font: { size: 11 }, color: '#6b7280', precision: 0 },
          },
        },
      },
    });
    this.charts.set('trends', chart);
  }

  private renderPeakHoursChart(): void {
    if (!this.peakHoursChartRef || !this.peakHours?.hours?.length) return;
    this.destroyChart('peakHours');
    const hours = this.peakHours.hours;
    const peakH = this.peakHours.peakHour.hour;
    const chart = new Chart(this.peakHoursChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: hours.map((h) => this.toChileHour(h.hour)),
        datasets: [
          {
            label: 'Conexiones',
            data: hours.map((h) => h.connections),
            backgroundColor: hours.map((h) =>
              h.hour === peakH ? CHART_COLORS.primary : 'rgba(52, 211, 153, 0.5)'
            ),
            borderColor: hours.map((h) =>
              h.hour === peakH ? CHART_COLORS.primary : CHART_COLORS.primaryLight
            ),
            borderWidth: 1,
            borderRadius: 4,
            hoverBackgroundColor: CHART_COLORS.accent,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1e293b',
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              title: (items) => `${items[0].label} hrs (Chile)`,
              label: (ctx) => {
                const h = hours[ctx.dataIndex];
                return [
                  `Conexiones: ${h.connections}`,
                  `Usuarios: ${h.uniqueUsers}`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 10 }, color: '#6b7280', maxRotation: 45 },
          },
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6' },
            ticks: { font: { size: 11 }, color: '#6b7280', precision: 0 },
          },
        },
      },
    });
    this.charts.set('peakHours', chart);
  }

  private renderSessionChart(): void {
    if (!this.sessionChartRef || !this.sessionDuration?.distribution?.length) return;
    this.destroyChart('session');
    const dist = this.sessionDuration.distribution;
    const gradientColors = ['#dc2626', '#f59e0b', '#34d399', '#059669', '#1C593D', '#0f172a'];
    const chart = new Chart(this.sessionChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: dist.map((d) => d.range.replace('(single connection)', '(única)')),
        datasets: [
          {
            label: 'Usuarios',
            data: dist.map((d) => d.count),
            backgroundColor: gradientColors.slice(0, dist.length),
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1e293b',
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => {
                const item = dist[ctx.dataIndex];
                return ` ${item.count} usuarios (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: '#f3f4f6' },
            ticks: { precision: 0, font: { size: 11 }, color: '#6b7280' },
          },
          y: {
            grid: { display: false },
            ticks: { font: { size: 11, weight: 'bold' }, color: '#374151' },
          },
        },
      },
    });
    this.charts.set('session', chart);
  }

  private renderDeviceTypeChart(): void {
    if (!this.deviceTypeChartRef || !this.byDevice?.byDeviceType?.length) return;
    this.destroyChart('deviceType');
    const data = this.byDevice.byDeviceType;
    const icons: Record<string, string> = { mobile: '📱', desktop: '💻', tablet: '📟' };
    const chart = new Chart(this.deviceTypeChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels: data.map((d) => `${icons[d.deviceType] || ''} ${d.deviceType}`),
        datasets: [
          {
            data: data.map((d) => d.uniqueUsers),
            backgroundColor: [CHART_COLORS.primary, CHART_COLORS.blue, CHART_COLORS.orange],
            borderWidth: 2,
            borderColor: '#fff',
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, pointStyle: 'circle', padding: 12, font: { size: 11, weight: 'bold' } },
          },
          tooltip: {
            backgroundColor: '#1e293b',
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => {
                const item = data[ctx.dataIndex];
                return ` ${item.uniqueUsers} (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
        },
      },
    });
    this.charts.set('deviceType', chart);
  }

  private renderOSChart(): void {
    if (!this.osChartRef || !this.byDevice?.byOS?.length) return;
    this.destroyChart('os');
    const data = this.byDevice.byOS;
    const chart = new Chart(this.osChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: data.map((d) => d.os),
        datasets: [
          {
            label: 'Usuarios',
            data: data.map((d) => d.uniqueUsers),
            backgroundColor: 'rgba(37, 99, 235, 0.7)',
            borderColor: CHART_COLORS.blue,
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1e293b',
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => {
                const item = data[ctx.dataIndex];
                return ` ${item.uniqueUsers} (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
        },
        scales: {
          x: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { precision: 0, font: { size: 11 }, color: '#6b7280' } },
          y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' }, color: '#374151' } },
        },
      },
    });
    this.charts.set('os', chart);
  }

  private renderBrowserChart(): void {
    if (!this.browserChartRef || !this.byDevice?.byBrowser?.length) return;
    this.destroyChart('browser');
    const data = this.byDevice.byBrowser;
    const chart = new Chart(this.browserChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: data.map((d) => d.browser),
        datasets: [
          {
            label: 'Usuarios',
            data: data.map((d) => d.uniqueUsers),
            backgroundColor: 'rgba(124, 58, 237, 0.7)',
            borderColor: CHART_COLORS.purple,
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1e293b',
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => {
                const item = data[ctx.dataIndex];
                return ` ${item.uniqueUsers} (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
        },
        scales: {
          x: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { precision: 0, font: { size: 11 }, color: '#6b7280' } },
          y: { grid: { display: false }, ticks: { font: { size: 11, weight: 'bold' }, color: '#374151' } },
        },
      },
    });
    this.charts.set('browser', chart);
  }

  private formatPeriodLabel(period: string): string {
    const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    if (period.length === 7) {
      const [y, m] = period.split('-');
      return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y.slice(2)}`;
    }
    const d = new Date(period + 'T00:00:00');
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  }
}
